import React from 'react';
import { palletsWord, vehiclesWord, loadedWord } from '../../utils/gazDeclensions.js';
import { formatMoney, formatNomenclatureName, formatNumber } from '../../utils/gazTextFormat.js';
import { isLightlyLoadedHaul, isWellLoadedHaul } from './gazVehicleLoadView.js';
import {
  GAZ_SLINGER_TITLE, GAZ_SLINGER_NOTE, GAZ_UNLOADING_SIDE_NOTE, GAZ_ACCESS_NOTE,
} from './gazSlingerNotice.js';
import './gazProposal.css';

// Карточки машин для расчёта МСК Газобетона. Рисует готовую модель из
// buildGazVehicleLoadModel: ничего не считает и транспорт не выбирает.

/**
 * Статус машины словами, а не только цветом: «полная», «загружена на 20%».
 * Неполная машина — это нормальный режим, а не ошибка.
 */
const statusTextOf = (vehicle) => {
  if (vehicle.isOverloaded) {
    return 'превышение';
  }
  if (vehicle.hasUnmeasuredCargo) {
    return 'место не учтено';
  }
  if (vehicle.isFull) {
    // Род по типу транспорта: шаланда полная, манипулятор полный.
    return loadedWord(vehicle.type) === 'загружена' ? 'полная' : 'полный';
  }
  return `${loadedWord(vehicle.type)} на ${vehicle.loadPercent}%`;
};

const toneOf = (vehicle, isLightlyLoaded) => {
  if (vehicle.isOverloaded) return 'error';
  if (vehicle.isFull) return 'full';
  // Меньше трети — красное: рейс оплачен целиком, а машина везёт воздух.
  if (isLightlyLoaded) return 'light';
  // Три четверти и больше — зелёное: рейс отработан почти полностью, пугать
  // оранжевым здесь нечем. Между порогами остаётся обычная неполная загрузка.
  if (isWellLoadedHaul(vehicle)) return 'well';
  return 'partial';
};

/**
 * Сколько ещё влезет в машину — одной строкой и с ответом «зачем»: место уже
 * оплачено рейсом.
 *
 * Текст один на всю область: его же показывает блок дозагрузки рядом с кнопкой,
 * поэтому числа не могут разойтись. В карточке место называется ровно один раз —
 * либо здесь, либо в блоке дозагрузки, если он есть.
 *
 * Ничего не считает: свободные места и объём приходят посчитанными.
 *
 * @returns {string|null} null, когда свободного места нет или оно недостоверно
 */
export const buildFreeSpaceText = (vehicle) => {
  if (!vehicle || vehicle.isFull || vehicle.hasUnmeasuredCargo) {
    return null;
  }
  const pallets = Number(vehicle.freePallets);
  if (!Number.isFinite(pallets) || pallets <= 0) {
    return null;
  }

  const parts = [`${formatNumber(pallets)} ${palletsWord(pallets)}`];
  if (vehicle.freeVolume !== null && vehicle.freeVolume !== undefined) {
    parts.push(`до ${formatNumber(vehicle.freeVolume)} м³`);
  }
  return `Можно добавить ${parts.join(' · ')} без дополнительного рейса`;
};

/**
 * Стропальщиков даёт заказчик.
 *
 * Стоит в карточке той машины, которая разгружает, — и когда манипулятор приехал
 * пустым только за разгрузкой, и когда он привёз товар и снимает его сам. У шаланд
 * и прочих машин, которые ничего не разгружают, блока нет.
 *
 * При разгрузке краном разгружает не машина заказа: кран приезжает сам по себе.
 * Своя карточка у него есть, но блока в ней нет — уточнение относится ко всей
 * разгрузке на объекте, поэтому стоит один раз под машинами, отдельным условием
 * разгрузки, а не свойством какой-то из них.
 *
 * Это уточнение к уже названной услуге, а не ошибка: спокойный жёлтый блок, без
 * красного состояния. Слова — общие с сообщением заказчику.
 */
const SlingerNotice = ({ standalone = false }) => (
  <p className={`gaz-veh__slinger${standalone ? ' gaz-veh__slinger--standalone' : ''}`}>
    <b>{GAZ_SLINGER_TITLE}</b>
    <span className="gaz-veh__slinger-note">{GAZ_SLINGER_NOTE}</span>
    {/* Где снимают груз — условие той же работы, поэтому строкой в том же блоке,
        а не отдельным предупреждением. */}
    <span className="gaz-veh__slinger-note">{GAZ_UNLOADING_SIDE_NOTE}</span>
  </p>
);

const VehicleCard = ({ vehicle, addUpsellSlot }) => {
  const isLightlyLoaded = isLightlyLoadedHaul(vehicle);
  const tone = toneOf(vehicle, isLightlyLoaded);
  // Блок дозагрузки сам называет свободное место рядом со своей кнопкой. Если он
  // есть — карточка это не повторяет.
  const upsellSlot = addUpsellSlot ? addUpsellSlot(vehicle) : null;
  const freeSpaceText = upsellSlot ? null : buildFreeSpaceText(vehicle);

  return (
    <article className={`gaz-veh gaz-veh--${tone}`}>
      {/* Процент загрузки называется один раз — в заголовке карточки. Там же
          метка машины, в которой много свободного места: внимания требует
          именно она, а не вариант заказа целиком. */}
      <header className="gaz-veh__head">
        <span className="gaz-veh__type">
          {vehicle.type}
          {vehicle.count > 1 && <span className="gaz-veh__count"> × {vehicle.count}</span>}
        </span>
        <span className="gaz-veh__status"> · {statusTextOf(vehicle)}</span>
        {isLightlyLoaded && <span className="gaz-veh__flag">Требует внимания</span>}
      </header>

      <div
        className="gaz-veh__meter"
        role="progressbar"
        aria-valuenow={vehicle.loadPercent}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`Загрузка: ${vehicle.type}, ${vehicle.loadPercent}%`}
      >
        <span style={{ width: `${Math.max(Math.min(vehicle.loadPercent, 100), vehicle.loadPercent > 0 ? 3 : 0)}%` }} />
      </div>

      {/* Занято и вместимость — одной строкой: «7/8 поддонов · 13,125/15 м³».
          Двух отдельных строк про поддоны и кубы больше нет. */}
      <p className="gaz-veh__load">
        <b>{formatNumber(vehicle.loadedPallets)}/{formatNumber(vehicle.capacity)}</b>{' '}
        {palletsWord(vehicle.capacity)}
        {vehicle.loadedVolume !== null && vehicle.maxVolume !== null && (
          <span className="gaz-veh__load-volume">
            {' · '}{formatNumber(vehicle.loadedVolume)}/{formatNumber(vehicle.maxVolume)} м³
          </span>
        )}
      </p>

      {/* Свободное место названо словами, а не только цветом: на печати и в
          чёрно-белом виде рамка пропадает, а строка остаётся. Недогрузка —
          возможность дозагрузить машину, поэтому строка оранжевая, а не красная. */}
      {freeSpaceText && (
        <p className={`gaz-veh__space${isLightlyLoaded ? ' gaz-veh__space--light' : ''}`}>
          {freeSpaceText}
        </p>
      )}

      {vehicle.hasUnmeasuredCargo && (
        <p className="gaz-veh__warn">
          Не посчитано место под: {vehicle.unmeasuredCargoNames.join(', ')} — в базе не заведена
          норма поддона. Проверьте загрузку вручную.
        </p>
      )}

      {/* Фактический груз машины. Строки приходят готовыми из модели — своего
          распределения и своих поддонов здесь нет. Газобетон только показывается:
          редактируется он в верхнем разделе «Товары». */}
      {Array.isArray(vehicle.cargoItems) && vehicle.cargoItems.length > 0 && (
        <div className="gaz-kp-cargo">
          <h4 className="gaz-kp-cargo__title">
            {vehicle.cargoNote || 'Груз в машине'}
          </h4>
          {vehicle.cargoItems.map((item, itemIndex) => (
            <div className="gaz-kp-cargo__row" key={`${item.name}-${itemIndex}`}>
              <div className="gaz-kp-cargo__name">{formatNomenclatureName(item.name)}</div>
              <div className="gaz-kp-cargo__meta">
                {[
                  `${formatNumber(item.volume)} м³`,
                  item.pallets !== null ? `${formatNumber(item.pallets)} ${palletsWord(item.pallets)}` : null,
                  item.pieces !== null ? `${formatNumber(item.pieces)} шт.` : null,
                ].filter(Boolean).join(' · ')}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Машина не только везёт, но и разгружает — значит уточнение о стропальщиках
          относится к ней. Стоит последним, после груза: сначала что везёт, потом
          что нужно от заказчика на объекте. */}
      {vehicle.unloadsCargo && <SlingerNotice />}

      {/* Место под строки допродаж и кнопку дополнения: наполняется вызывающим
          кодом только тогда, когда варианты товаров действительно есть. */}
      {upsellSlot}
    </article>
  );
};

/**
 * Техника, приехавшая без груза только за разгрузкой: манипулятор или кран.
 *
 * Это услуга, а не рейс с товаром, поэтому карточка компактная и намеренно не
 * повторяет ничего от грузовой машины: ни полосы и процента загрузки, ни
 * поддономест, ни состава груза, ни предупреждения о недогрузке, ни дозагрузки —
 * везти ей нечего, и свободного места у неё не бывает.
 *
 * Карточка одна на оба вида техники: название, объём работ и сумма приходят из
 * модели, своей ветки разметки под кран нет. Отличаются они только уточнением о
 * стропальщиках: манипулятор разгружает сам и носит его в своей карточке, а под
 * кран тот же блок стоит общим — ниже, под списком машин. Что показывать, решает
 * модель, а не разметка.
 *
 * Поддоны и сумма приходят посчитанными из расчёта; разгрузка уже входит в его
 * итог, и карточка её только называет.
 */
const UnloadingServiceCard = ({ service }) => (
  <article className="gaz-veh gaz-veh--service">
    <header className="gaz-veh__head">
      <span className="gaz-veh__type">{service.type}</span>
      <span className="gaz-veh__status"> · только разгрузка</span>
    </header>

    <p className="gaz-veh__service-note">Приедет без груза и разгрузит заказ</p>

    {(service.unloadedPallets !== null || service.unloadingCost !== null) && (
      <p className="gaz-veh__service-facts">
        {[
          service.unloadedPallets !== null
            ? `Разгрузит ${formatNumber(service.unloadedPallets)} ${palletsWord(service.unloadedPallets)}`
            : null,
          service.unloadingCost !== null
            ? `Разгрузка ${formatMoney(service.unloadingCost)} ₽`
            : null,
        ].filter(Boolean).join(' · ')}
      </p>
    )}

    {service.showsSlingers && <SlingerNotice />}
  </article>
);

// Показала ли предупреждение какая-нибудь карточка: у манипулятора оно стоит
// внутри карточки, и дублировать его под списком не нужно. Карточка крана его не
// показывает, поэтому наличие услуги само по себе ничего не закрывает — спрашиваем
// у неё саму.
const hasCardWithSlingers = (model) => Boolean(model.unloadingService?.showsSlingers)
  || (model.vehicles || []).some((vehicle) => vehicle.unloadsCargo);

const GazVehicleLoadVisual = ({ model, addUpsellSlot }) => {
  if (!model || !Array.isArray(model.vehicles) || model.vehicles.length === 0) {
    return null;
  }

  return (
    <section className="gaz-vehs" aria-label="Распределение по машинам">
      {/* Сколько машин везёт груз — и, отдельно, приезжает ли за ними техника
          разгружать: манипулятор или кран. Она не рейс с товаром, поэтому в число
          машин не входит и названа словами. Название берётся из модели: своего
          списка видов техники у заголовка нет. */}
      <h3 className="gaz-vehs__title">
        Доставка: {model.totalVehicles} {vehiclesWord(model.totalVehicles)}
        {model.unloadingService && ` + ${model.unloadingService.type.toLowerCase()} на разгрузку`}
      </h3>
      <div className="gaz-vehs__grid">
        {model.vehicles.map((vehicle, index) => (
          <VehicleCard
            key={`${vehicle.type}-${index}`}
            vehicle={vehicle}
            addUpsellSlot={addUpsellSlot}
          />
        ))}
        {/* После грузовых машин: сначала чем везут, потом кто разгружает. */}
        {model.unloadingService && <UnloadingServiceCard service={model.unloadingService} />}
      </div>

      {/* Разгрузка есть, но ни одна карточка предупреждения не показала — так
          бывает при разгрузке краном: карточка у крана своя, а уточнение относится
          ко всей разгрузке. Тогда оно стоит под машинами и ровно один раз:
          карточки, которые уже его показали, второй копии не получают. */}
      {model.needsSlingers && !hasCardWithSlingers(model) && <SlingerNotice standalone />}

      {/* Подъезд к объекту — условие доставки, а не разгрузки: показывается всегда,
          одной спокойной строкой под машинами. Ни шаланда, ни манипулятор не везде
          проходят, и тогда цену доставки пересогласовывает логист. */}
      <p className="gaz-vehs__access">{GAZ_ACCESS_NOTE}</p>
    </section>
  );
};

export default GazVehicleLoadVisual;
